import ProjectWorld, { createServerEvent, ProjectEventType, type ShareLink } from "../ProjectWorld";
import type { BaseEvent, BaseProjectionResult, BaseEventInput, EventProcessor } from "@utilitydelta/clientside-event-sourcing";
import { AccessLevel, ServerEvent } from "@utilitydelta/eventplanedb-client";
import log from "loglevel";

export const MINOR_VERSION: number = 1;

export interface AddShareLink_LocalEvent extends BaseEvent<ProjectEventType> {
    tp: typeof ProjectEventType.AddShareLink;
    shareLinkId: string; // Unique ID for the share link
    accessLevel: AccessLevel; // Level of access granted
    expiresOn: number; // Expiration timestamp
    isSingleUse: boolean;
    description?: string; // Optional description
}

export interface AddShareLink_ProjectionResult extends BaseProjectionResult {
    kind: 'AddShareLink_ProjectionResult';
    shareLink: ShareLink; // The created share link
}

export interface AddShareLink_EventInputs extends BaseEventInput {
    kind: 'AddShareLink_EventInputs';
    shareLinkId: string; // Unique ID for the share link
    accessLevel: AccessLevel; // Level of access granted
    expiresOn: number; // Expiration timestamp
    description?: string; // Optional description
    isSingleUse: boolean;
}

export type AddShareLink_EventProcessorType = EventProcessor<ProjectEventType, AddShareLink_EventInputs, AddShareLink_LocalEvent, AddShareLink_ProjectionResult, AddShareLink_LocalEvent, ServerEvent<ProjectEventType>>;

export class AddShareLink_EventProcessor implements AddShareLink_EventProcessorType {

    private projectWorld: ProjectWorld;
    constructor(pw: ProjectWorld) { this.projectWorld = pw; }

    public get shouldEncrypt(): boolean { return true; }
    public get eventType(): ProjectEventType { return ProjectEventType.AddShareLink; }
    public get eventTypeMinorVersion(): number { return MINOR_VERSION; }
    public get undoEventType(): ProjectEventType { return ProjectEventType.AddShareLink; }
    public get undoEventTypeMinorVersion(): number { return MINOR_VERSION; }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createUndoEvent = (_undoEvent: AddShareLink_LocalEvent, _originalEventProjectionResult: AddShareLink_ProjectionResult): boolean => {
        return false;
    }

    public projectOntoWorld = (event: AddShareLink_LocalEvent, addUpdatedEntitiesToDirtySet: boolean, returnProjectionResults: boolean): AddShareLink_ProjectionResult | undefined => {
        // Check if share link already exists
        let entity = this.projectWorld.shareLinks.maybeGet(event.shareLinkId);
        if (entity) {
            log.warn("Share link already exists", event);
            return undefined;
        }

        // Create new entity
        entity = {
            id: event.shareLinkId,
            isDeleted: false,
            pi: this.projectWorld.projectMetadata.id,
            lastEventTime: { [event.tp]: event.et } as Record<ProjectEventType, number>,
            createdByClientId: event.ci,
            createdByUserId: event.ui,
            createdOn: event.et,
            modifiedByClientId: event.ci,
            modifiedByUserId: event.ui,
            modifiedOn: event.et,
            isActive: true,
            isSingleUse: false,
            accessLevel: event.accessLevel,
            description: event.description,
            expiresOn: event.expiresOn,
            usageCount: 0,
        };

        this.projectWorld.shareLinks.upsert(entity);

        if (addUpdatedEntitiesToDirtySet) {
            this.projectWorld.addEntityToSave('shareLinks', entity);
        }
        return !returnProjectionResults ? undefined : {
            kind: 'AddShareLink_ProjectionResult',
            shareLink: entity,
        };
    }

    public createLocalEvent = (eventToPopulate: AddShareLink_LocalEvent, userInputs: AddShareLink_EventInputs): boolean => {
        const entity = this.projectWorld.shareLinks.maybeGet(userInputs.shareLinkId);
        if (entity !== undefined) {
            log.warn("Share link already exists", userInputs.shareLinkId);
            return false;
        }

        eventToPopulate.shareLinkId = userInputs.shareLinkId;
        eventToPopulate.accessLevel = userInputs.accessLevel;
        eventToPopulate.expiresOn = userInputs.expiresOn;
        eventToPopulate.description = userInputs.description;
        eventToPopulate.isSingleUse = userInputs.isSingleUse;

        return true;
    }

    public toServerEvent = async (localEvent: AddShareLink_LocalEvent): Promise<ServerEvent<ProjectEventType>> => {
        const eventData = {
            shareLinkId: localEvent.shareLinkId,
            accessLevel: localEvent.accessLevel,
            expiresOn: localEvent.expiresOn,
            description: localEvent.description,
            isSingleUse: localEvent.isSingleUse,
        };
        const eventDataString = JSON.stringify(eventData);
        return createServerEvent(localEvent, eventDataString);
    }

    public fromServerEvent = async (localEventToPopulate: AddShareLink_LocalEvent, serverEvent: ServerEvent<ProjectEventType>): Promise<void> => {
        const eventData = JSON.parse(atob(serverEvent.ev))
        Object.assign(localEventToPopulate, eventData);
    }
}